import { uuidv4 } from "./utils"

export default class Deepgram {
    constructor(opts) {
        this.Opts = opts
        this.AccessKey = opts?.key
        this.WS = this.initWS()
        this.started = false
        this.lenOfChunks = 0
        this.latestEnd = 0
        this.transcript = {} // transcripts {transcript_id: { transcript data (summary a few chunks) }, ...}
        this.transcriptIds = [] // IDs of current prase transcription [channel_index: id of current phrase]
        this.transcriptChunks = {} // IDs of current chunk transcription: {chanel_index: { start_time: { chunk transcript data } }, ...}
        this.roles = [] // [channel_index: role]

        window.addEventListener("transcriber_split_transcription", () => this.splitTranscription())
    }

    initWS = () => {
        const params = {
            model: "nova-2",
            language: "en-US",
            smart_format: true,
            punctuate: true,
            interim_results: true,
            // endpointing: 50,
            // sample_rate: 48000,
            encoding: "opus", // "mulaw", //"linear16",
            utterance_end_ms: 1200,
            multichannel: true,
            channels: 2,
            // vad_events: true,
        }
        const urlParams = new URLSearchParams([...Object.entries(params)])
        const ws = new WebSocket(`wss://api.deepgram.com/v1/listen?${urlParams}`, [
            this.AccessKey?.length > 64 ? "bearer" : "token",
            this.AccessKey,
        ])
        ws.onopen = () => this.Start()
        ws.onerror = () => (this.started = false)
        ws.onclose = () => (this.started = false)
        ws.onmessage = event => this.onMessage(event)
        return ws
    }

    onMessage = event => {
        const msg = JSON.parse(event.data)
        switch (msg.type) {
            case "Results":
                // skip if transcription is empty
                if (!msg?.channel?.alternatives[0]?.transcript) return

                // skip if we already gotten a final transcript
                if (this.transcriptChunks?.[msg.channel_index[0]]?.[msg.start]?.is_final) return

                // make a chunk
                const chunk = {
                    start: msg.start,
                    end: msg.start + msg.duration,
                    duration: msg.duration,
                    is_final: msg.is_final,
                    text: msg.channel.alternatives[0].transcript,
                    channel: msg.channel_index[0],
                }

                // set the end of conversation
                if (this.latestEnd < chunk.end) {
                    this.latestEnd = chunk.end
                }

                // save chunk
                if (!this.transcriptChunks?.[chunk.channel]) {
                    this.transcriptChunks[chunk.channel] = {}
                }
                this.transcriptChunks[chunk.channel][chunk.start] = chunk

                this.summarizeTranscriptChunks(chunk.end, chunk.channel, false)
                break

            case "UtteranceEnd":
                // const transcriptId = this.transcriptIds[msg.channel[0]]
                this.summarizeTranscriptChunks(msg.last_word_end, msg.channel[0], true)
                break

            default:
                console.log("unknown message", msg)
        }
    }

    OnLine = () => this.WS?.readyState === WebSocket.OPEN

    Start = () => {
        this.started = true
        this.StartKeepAlive()
    }

    Send = chunk => {
        if (this.OnLine() && this.started) {
            if (!this.firstChunk) {
                return
            } else if (this.firstChunk !== "sended") {
                this.WS.send(this.firstChunk)
                this.firstChunk = "sended"
                return
            }
            this.WS.send(chunk)
        }
    }

    Finish = () => {
        if (this.OnLine() && this.started) {
            this.WS.send(JSON.stringify({ type: "CloseStream" }))
        }
        this.StopKeepAlive()
        this.mediaRecorder.stop()
        this.audioContext.close()
        this.WS.close()
        window.removeEventListener("transcriber_split_transcription", () => this.splitTranscription())
    }

    StartKeepAlive = () => {
        if (this.keepAlive) return
        this.keepAlive = setInterval(() => {
            this.WS.send(JSON.stringify({ type: "KeepAlive" }))
        }, 3000)
    }

    StopKeepAlive = () => {
        if (!this.keepAlive) return
        clearInterval(this.keepAlive)
        this.KeepAlive = null
    }

    summarizeTranscriptChunks = (end, channel, final, recursive = false) => {
        if (!this.transcriptChunks[channel]) return

        const transcript = {
            role: this.roles[channel],
            text: "",
            id: this.transcriptIds[channel],
            ts: this.transcript[this.transcriptIds[channel]]?.ts || new Date().getTime(),
            channel,
        }

        for (const chunk of Object.values(this.transcriptChunks[channel])) {
            // if we out of summarize range or another channel, then skip this chunk
            if (chunk.start >= end || chunk.channel !== channel) continue

            // skip chunks which is not a final, this chunk should to go to the next summary or should be skipped forever
            if (final && !chunk.is_final) continue

            // set a start [in sec] of summary transcript
            if (transcript.start === undefined) transcript.start = chunk.start

            // set a end [in sec] of summary transcript, take a max end of chunks range
            if (transcript.end === undefined || chunk.end > transcript.end) transcript.end = chunk.end

            transcript.text = (transcript.text + " " + chunk.text).trim()

            // we're build final version of transcription, delete already used chunks
            if (final) {
                delete this.transcriptChunks[channel][chunk.start]
            }
        }

        // skip if nothing to summarize
        if (transcript.text.length === 0) {
            return
        }

        // update full transcript
        this.transcript[this.transcriptIds[channel]] = transcript

        if (final || recursive) {
            this.transcriptIds[channel] = uuidv4()
        }

        window.dispatchEvent(
            new CustomEvent("call_intelligence_call_transcription", {
                detail: transcript,
            })
        )

        // build the final summary for other channels (conversation builder)
        if (!recursive && this.roles.length > 1) {
            for (let i = 0; i < this.roles.length; i++) {
                if (i === channel) continue
                this.summarizeTranscriptChunks(end, i, true, true)
            }
        }
    }

    splitTranscription = () => {
        for (let i = 0; i < this.roles.length; i++) {
            this.summarizeTranscriptChunks(this.latestEnd, i, true, true)
        }
    }

    async AddAudioTrack(role, audioTrack) {
        this.roles.push(role)
        this.transcriptIds.push(uuidv4())

        if (!this.audioContext) {
            this.audioContext = new AudioContext()
            this.mixedStream = this.audioContext.createMediaStreamDestination()
            this.mergerStream = this.audioContext.createChannelMerger(2)
            this.mergerStream.channelInterpretation = "speakers"
            this.mergerStream.connect(this.mixedStream)
        }

        const asID = this.roles.indexOf(role)
        const audioMediaStream = new MediaStream([audioTrack])
        const audioSource = this.audioContext.createMediaStreamSource(audioMediaStream)
        audioSource.connect(this.mergerStream, 0, asID)

        if (!this.mediaRecorder) {
            this.mediaRecorder = new MediaRecorder(this.mixedStream.stream)

            this.mediaRecorder.start(250)
            this.mediaRecorder.ondataavailable = async event => {
                if (event.timecode === 0) {
                    const blob = await event.data.arrayBuffer()
                    this.firstChunk = blob
                    this.Send(blob)
                } else if (event.data.size > 0) {
                    const blob = await event.data.arrayBuffer()
                    this.Send(blob)
                }
            }
        }
    }
}
